import {getPlantById} from '@hconnect/apiclient'
import {Button, GenericInput} from '@hm/fundamental'
import {mdiMagnify} from '@mdi/js'
import React, {Reducer, useReducer, useState} from 'react'

import {api} from '../App.store'
import {useTranslation} from '../hooks/useTranslation'
import {GroupedRoleAssignment} from '../modules/ManageUsers.selectors'

import {
  initialTextFieldState,
  LookupState,
  TextFieldAction,
  textFieldReducer
} from './TextFieldReducer'

type RolePlantLookupTextFieldProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  onChangePlantIds: (plantIds: string) => void
  dataScopeName: string
  disabled: boolean
  helperText?: string
}

export const RolePlantLookupTextField = ({
  dataScopeName,
  onChangePlantIds,
  disabled,
  helperText,
  groupedRoleAssignment
}: RolePlantLookupTextFieldProps) => {
  const {t} = useTranslation()

  const [text, setText] = useState('')
  const [state, dispatch] = useReducer<Reducer<LookupState, TextFieldAction>>(
    textFieldReducer,
    initialTextFieldState
  )
  const {countryId} = groupedRoleAssignment.commonDataScopes
  const handleLookup = async () => {
    dispatch({type: 'LOOKUP_REQUEST'})
    if (text.trim().length === 0) {
      return
    }
    if (dataScopeName === 'plantIds') {
      try {
        const plant = await getPlantById(api)(text, countryId as string)
        if (
          groupedRoleAssignment.variantDataScopes.plantIds?.some(
            (plantId) => plantId === plant.plantId
          )
        ) {
          dispatch({
            type: 'LOOKUP_FAILURE',
            payload: new Error(t('roleAssignment.duplicatedPlant')),
            error: true
          })
          return
        }

        if (plant?.plantId && plant?.country === countryId) {
          dispatch({type: 'LOOKUP_SUCCESS'})
          onChangePlantIds(plant.plantId)
          return
        }
        throw new Error('Invalid country!')
      } catch (err) {
        dispatch({
          type: 'LOOKUP_FAILURE',
          payload: new Error(t(`roleAssignment.noLookupResult.${dataScopeName}`)),
          error: true
        })
        return
      }
    }
    setText('')
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    void handleLookup()
  }

  return (
    <div className="flex gap-2 w-full max-w-[600px]">
      <GenericInput
        label={t(`roleAssignment.dataScope.${dataScopeName}`)}
        data-test-id={`${dataScopeName}-textField`}
        value={text}
        onChange={(value) => setText(value)}
        onKeyDown={handleKeyDown}
        type="search"
        isDisabled={disabled}
        isTouched={true}
        error={state.errorMessage || helperText}
        className="flex-1"
      />
      <Button
        data-test-id="button-plant-lookup"
        variant="secondary"
        icon={mdiMagnify}
        type="button"
        className="h-[48px]"
        onClick={handleLookup}
        disabled={disabled}
      >
        Verify
      </Button>
    </div>
  )
}
